/**
 * 다른 앱에서 공유받은 글을 인용으로 다듬는다.
 *
 * 전자책 앱은 문장 뒤에 책 제목·저자·앱 이름·링크를 붙여 보내는 일이 많다.
 * 교보eBook 이 정확히 어떤 모양으로 보내는지는 확인하지 못해서, 특정 형식에 맞추지 않고
 * '짧고 출처처럼 생긴 줄'만 떼어 낸다. 받은 원문은 화면에서 언제든 되돌릴 수 있다.
 */

const URL_RE = /https?:\/\/\S+/gi;
// 출처 줄에만 나올 말. '중에서'·'발췌' 처럼 본문에도 흔한 말은 넣지 않는다.
// 이 말이 섞인 긴 문장은 출처로 보지 않는다 (MAX_KEYWORD_SOURCE_LINE)
const SOURCE_WORD_RE = /(교보\s*e\s*book|교보문고|kyobo|e\s*book\s*에서|출처\s*[:：])/i;
// 출처 줄은 보통 대시나 괄호로 시작한다: '- 책 제목, 저자', '『책 제목』'
const SOURCE_LEAD_RE = /^[-–—―~•·<《『「[(]/;
// 출처 줄 길이 상한. 앱 이름으로 알아보는 줄은 '[교보eBook] 책 제목' 정도라 더 짧게 잡는다 —
// 그보다 길면 앱 이름이 우연히 섞인 본문 문장일 가능성이 크다
const MAX_SOURCE_LINE = 80;
const MAX_KEYWORD_SOURCE_LINE = 40;

const QUOTE_PAIRS = [['"', '"'], ['“', '”'], ['‘', '’'], ["'", "'"], ['「', '」'], ['『', '』'], ['《', '》'], ['<', '>']];

/** 비교용 — 띄어쓰기와 문장부호를 빼고 소문자로 */
export const normalizeForMatch = (value = '') =>
  String(value).toLowerCase().replace(/[\s\p{P}\p{S}]/gu, '');

/** 글 전체를 감싼 따옴표 한 쌍만 벗긴다 ('"A" 라고 했다' 처럼 일부만 감싼 것은 그대로) */
export const stripWrappingQuotes = (text = '') => {
  const trimmed = text.trim();
  const pair = QUOTE_PAIRS.find(([open, close]) =>
    trimmed.length > 2 && trimmed.startsWith(open) && trimmed.endsWith(close)
  );
  if (!pair) return trimmed;
  const inner = trimmed.slice(pair[0].length, trimmed.length - pair[1].length);
  // 안에 같은 따옴표가 또 있으면 감싼 게 아니라 두 인용이 나란히 있는 것이다
  return inner.includes(pair[1]) ? trimmed : inner.trim();
};

/** 출처 줄에서 쪽수를 찾는다: 'p.132', 'P 132', '132쪽', '132 페이지' */
export const findPageNumber = (text = '') => {
  const match = String(text).match(/\bp\.?\s*(\d{1,4})\b/i) || String(text).match(/(\d{1,4})\s*(?:쪽|페이지)/);
  return match ? match[1] : '';
};

// 제목 바로 뒤에 이런 글자가 오면 '제목, 저자' 같은 출처 줄이다.
// 조사(는·은·의·을…)가 오면 문장 속에서 제목을 말한 것이라 본문으로 둔다
const AFTER_TITLE_RE = /^\s*(?:$|[,/|·:\-–—(（[<『「])/;

const isSourceLine = (line, titles) => {
  if (!line || line.length > MAX_SOURCE_LINE) return false;
  if (SOURCE_WORD_RE.test(line) && line.length <= MAX_KEYWORD_SOURCE_LINE) return true;

  const key = normalizeForMatch(line);
  return titles.some(title => {
    // '- 책 제목, 저자' · '『책 제목』' — 대시나 괄호로 시작하고 제목이 든 줄
    if (SOURCE_LEAD_RE.test(line) && key.includes(normalizeForMatch(title))) return true;
    // '책 제목 / 저자' · '책 제목' — 제목으로 시작하고 뒤에 구분자만 오는 줄
    return line.toLowerCase().startsWith(title.toLowerCase())
      && AFTER_TITLE_RE.test(line.slice(title.length));
  });
};

/**
 * 공유받은 글에서 인용 본문만 남긴다.
 * @param {string} text 받은 글
 * @param {{ titles?: string[] }} options 고른 책의 제목들(원래 제목·부제를 뗀 제목) —
 *   그 제목으로 된 출처 줄을 떼어 낸다
 * @returns {{ quote: string, page: string }} page 는 출처 줄에 쪽수가 있을 때만
 */
export const cleanSharedQuote = (text = '', { titles = [] } = {}) => {
  // 두 글자 이하 제목은 본문 첫머리와 우연히 겹치기 쉽다
  const usableTitles = [...new Set(titles.map(t => String(t || '').trim()))]
    .filter(t => normalizeForMatch(t).length >= 3);
  const body = [];
  const sources = [];

  String(text).replace(/\r\n?/g, '\n').split('\n').forEach(raw => {
    const line = raw.replace(URL_RE, '').trim();
    if (raw.trim() && !line) return; // 링크만 있던 줄
    if (isSourceLine(line, usableTitles)) sources.push(line);
    else body.push(line);
  });

  const quote = stripWrappingQuotes(
    body.join('\n').replace(/\n{3,}/g, '\n\n').trim()
  );
  return { quote, page: findPageNumber(sources.join(' ')) };
};

/**
 * 공유받은 글에 제목이 들어 있는 책을 찾는다.
 * 두 글자 이하 제목은 우연히 겹치기 쉬워 보지 않는다. 여럿이 걸리면 가장 긴 제목,
 * 길이가 같으면 지금 읽는 책을 고른다.
 *
 * @param {Array} books
 * @param {string} shared 받은 글 (제목·본문·이미지에서 읽은 제목을 모두 이어 붙인 것)
 * @param {(title:string)=>string} shorten 부제를 떼는 함수 (TitleEditor 의 shortenTitle)
 * @param {(book)=>boolean} isReading
 */
export const findSharedBook = (books = [], shared = '', shorten = (t) => t, isReading = () => false) => {
  const haystack = normalizeForMatch(shared);
  if (!haystack) return null;

  let best = null;
  let bestScore = 0;
  books.forEach(book => {
    const keys = [book.title, shorten(book.title || '')]
      .map(normalizeForMatch)
      .filter(key => key.length >= 3);
    const score = Math.max(0, ...keys.filter(key => haystack.includes(key)).map(key => key.length));
    if (score === 0) return;
    const better = score > bestScore || (score === bestScore && isReading(book) && !isReading(best));
    if (better) {
      best = book;
      bestScore = score;
    }
  });
  return best;
};
